import React, { useContext } from 'react';
//gatsby
import { useStaticQuery, graphql } from 'gatsby';
//router
import { useLocation } from '@reach/router';
//context
import { GlobalContext } from 'context/';
//styles
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import vars from 'components/styles/varss';
//components
import Container from 'components/ui/container';
import NavToggle from 'components/ui/mobileNavToggle';
import Menu from 'components/ui/menu';
//hooks
import { useWindowSize } from 'components/hooks/useWindowSize';

//styled
const HeaderWrap = styled.header`
  display: flex;
  align-items: center;
  background-color: ${props => props.headerBg};
  height: ${vars.headerHeight}px;
  position: relative;
  z-index: 99;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  .move-down{
    margin-top: ${vars.movingDown}px;
  }
  @media (max-width: ${vars.navBreakpoint}px) {
    height: ${vars.headerHeightSm}px;
  }  

  ${Container} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    @media(min-width: ${vars.media.xxlMin}) {
      max-width: 1520px;
    }
  }
  /* colors */
  .logo {
    path {
      fill: ${props => props.headerColor};
    }
  }
  .banner-top {
    background: #003E56;
  }
  .banner-top-text {
    display: block;
    font-size: ${rem('12px')};
    border-radius: 12px;
    font-weight: 700;
    top: 0;
    left: 50%;
    padding: 8px 12px;
    position: absolute;
    transform: translateY(-100%);
    transition: transform 0.3s;
    z-index: 99999;
  }
  .nav-menu {
    > li {
      > a:not(.button) {
        color: ${props => props.headerColor};
      }
      .nav-dropdown {
        > a {
          color: ${props => props.headerColor};
        }
      }
    }
  }
  .nav-toggle-line {
    background-color: ${props => props.headerColor}!important;
  }
  /* mobile nav active */
  ${props => props.mobileNav && css`
    background-color: ${vars.colors.darkBlue};
    .logo {
      path {
        fill: #ffffff!important;
      }
    }
    .nav-toggle-line {
      background-color: #ffffff!important;
    }
  `}
`;

const Logo = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  a {
    display: block;
    svg {
      @media(max-width: ${vars.media.lgMax}) {
        width: 166px;
        height: 30px;
      }
    }
  }
  > p {
    font-weight: bold;
    color: ${vars.colors.purple};
    margin-left: ${rem('12px')};
    letter-spacing: -0.008em;
    line-height: 1.6;
  }
`;

const SkipToContent = styled.a`
  display: block;
  font-size: ${rem('16px')};
  background: ${vars.colors.green};
  border-radius: 12px;
  font-weight: 700;
  top: 0;
  left: 50%;
  padding: 8px 12px;
  position: absolute;
  transform: translateY(-100%);
  transition: transform 0.3s;
  z-index: 99997;
  &:focus {
    transform: translateY(0%);
  }
`;

const Header = ({ isResources, headerBgFromPage, headerColorFromPage }) => {

  //graphql query
  const data = useStaticQuery(graphql`
    query {
      wp {
        generalSettings {
          title
          description
        }
      }
      Logo: file(relativePath: {eq: "Refersion-logo.svg"}) {
        publicURL
      }
      MenuCenter: allWpMenuItem(filter: {locations: {eq: HEADER_MENU_CENTER}, parentDatabaseId: {eq: 0}}) {
        nodes {
          id
          label
          path
          description
          target
          cssClasses
          iconClass
          childItems {
            nodes {
              id
              label
              path
              description
              target
              cssClasses
              iconClass
              childItems {
                nodes {
                  id
                  label
                  path
                  description
                  target
                  cssClasses
                  iconClass
                }
              }
            }
          }
        }
      }
      MenuRight: allWpMenuItem(filter: {locations: {eq: HEADER_MENU_RIGHT}, parentDatabaseId: {eq: 0}}) {
        nodes {
          id
          label
          path
          description
          target
          cssClasses
          iconClass
          childItems {
            nodes {
              id
              label
              path
              description
              target
              cssClasses
              iconClass
            }
          }
        }
      }
      MenuResources: allWpMenuItem(filter: {locations: {eq: RESOURCES_MENU}, parentDatabaseId: {eq: 0}}) {
        nodes {
          id
          label
          path
          description
          target
          cssClasses
          iconClass
          childItems {
            nodes {
              id
              label
              path
              description
              target
              cssClasses
              iconClass
            }
          }
        }
      }
    }
  `);

  //use Context
  const [context, setContext] = useContext(GlobalContext);

  //close mobile nav
  const closeMobileNav = () => {
    setContext({
      ...context,
      mobileNav: false,
      mobilePanel: false,
    });
  };

  //window size
  const windowSize = useWindowSize();

  //get url params
  const location = useLocation()
  const search = location.search;
  return (
    <>
      <SkipToContent href="#app-main">Skip to content</SkipToContent>
      <HeaderWrap
        mobileNav={context.mobileNav}
        headerBg={headerBgFromPage}
        headerColor={headerColorFromPage}>
        <Container maxWidth="1352px">
          <Logo onClick={closeMobileNav}>
            <a href={`/${search}`} target="_blank" rel="noopener noreferrer" aria-label="Home Link">
              <svg width="223" height="50" viewBox="0 0 223 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="logo" role="img">
                <g clipPath="url(#clip0_5_61)">
                  <path fillRule="evenodd" clipRule="evenodd" d="M98.6852 9.35101C98.7353 6.96929 99.8038 5.21098 103.143 5.21098H104.479V-1.52588e-05H101.991C96.064 -1.52588e-05 92.8584 3.4207 92.8584 9.38298V10.8376H88.935V15.1854H92.8584V30.7545H98.7019V15.1854H103.243V10.8376H98.7019L98.6852 9.35101ZM49.5336 20.2206L56.8462 30.7545H64.1088L56.1784 19.9648C60.5025 19.2455 63.5745 15.1375 63.5745 10.9655C63.5745 6.13809 60.5359 1.35868 53.5071 1.35868H41.2359V30.7545H47.4299V20.2206H49.5336ZM47.4299 15.4412V6.61763H52.8727C55.7777 6.61763 57.3304 8.74359 57.3304 11.0933C57.3304 13.4431 55.8612 15.4412 52.3885 15.4412H47.4299ZM154.398 31.282C150.024 31.282 145.8 28.9002 145.75 24.5045H151.226C151.226 25.6554 152.244 26.8862 154.298 26.8862C155.984 26.8862 157.286 26.071 157.286 24.9201C157.286 24.0729 156.569 23.6413 155.016 23.2577L152.244 22.6183C146.902 21.2596 146.184 18.3983 146.184 16.3043C146.184 13.2353 149.523 10.3261 154.348 10.3261C158.138 10.3261 162.596 12.0684 162.546 16.8158H156.969C156.969 15.4571 155.817 14.7219 154.431 14.7219C153.096 14.7219 152.027 15.4412 152.027 16.4642C152.027 17.4073 152.912 17.8708 154.081 18.1745L157.52 19.0217C162.512 20.3005 163.13 23.2896 163.13 24.9041C163.147 29.108 158.689 31.282 154.398 31.282ZM166.035 3.96418C166.035 5.83438 167.721 7.33694 169.741 7.33694C171.795 7.33694 173.448 5.85037 173.448 3.96418C173.448 2.09397 171.795 0.639371 169.741 0.639371C167.721 0.639371 166.035 2.09397 166.035 3.96418ZM166.836 10.8376V30.7704H172.68V10.8376H166.836ZM187.539 31.282C181.612 31.282 176.353 27.094 176.353 20.828C176.353 14.5141 181.612 10.3261 187.539 10.3261C193.466 10.3261 198.725 14.5141 198.725 20.828C198.725 27.094 193.466 31.282 187.539 31.282ZM187.539 26.071C190.444 26.071 192.798 23.897 192.798 20.828C192.798 17.711 190.444 15.5371 187.539 15.5371C184.65 15.5371 182.28 17.711 182.28 20.828C182.28 23.897 184.65 26.071 187.539 26.071ZM222.75 19.8369C222.8 13.8587 219.945 10.3261 214.502 10.3261C212.448 10.3261 209.159 11.5249 208.258 14.2103V10.8376H202.414V30.7704H208.258V20.3165C208.258 16.688 210.846 15.617 212.933 15.617C214.936 15.617 216.906 17.0236 216.906 20.1406V30.7704H222.75V19.8369ZM135.699 13.6669C137.602 11.2212 140.007 9.99039 142.928 9.99039V15.7129C140.641 15.7129 138.855 16.2564 137.569 17.3433C136.317 18.4303 135.699 19.8849 135.699 21.7391V31.3139H129.889V10.3261H135.682L135.699 13.6669ZM88.0167 22.5543C88.1336 22.1387 88.1837 21.5633 88.1837 20.828C88.1837 17.8229 87.0985 15.3293 84.9281 13.3312C82.791 11.3171 80.1198 10.3101 76.8975 10.3101C73.692 10.3101 71.004 11.3171 68.8336 13.3312C66.6966 15.3133 65.6281 17.8069 65.6281 20.78C65.6281 23.7532 66.7133 26.2628 68.8837 28.2768C71.0541 30.2589 73.7588 31.25 76.9977 31.25C79.5187 31.25 81.7559 30.6426 83.7093 29.4117C85.6627 28.1809 87.0484 26.5665 87.8665 24.5524H82.0564C81.589 25.2078 80.9044 25.7353 80.0029 26.1669C79.118 26.5665 78.1497 26.7583 77.0812 26.7583C75.6621 26.7583 74.4266 26.3747 73.4249 25.6234C72.4064 24.8721 71.7219 23.8491 71.3713 22.5703L88.0167 22.5543ZM76.9142 14.7858C78.2332 14.7858 79.3852 15.1534 80.3702 15.8887C81.3886 16.624 82.0731 17.5991 82.4237 18.8299H71.4381C71.7887 17.5991 72.4732 16.624 73.4582 15.8887C74.4433 15.1534 75.5953 14.7858 76.9142 14.7858ZM126.851 22.5543C126.967 22.1387 127.017 21.5633 127.017 20.828C127.017 17.8229 125.932 15.3293 123.762 13.3312C121.625 11.3171 118.954 10.3101 115.731 10.3101C112.526 10.3101 109.838 11.3171 107.667 13.3312C105.53 15.3133 104.462 17.8069 104.462 20.78C104.462 23.7532 105.547 26.2628 107.717 28.2768C109.888 30.2589 112.593 31.25 115.831 31.25C118.352 31.25 120.59 30.6426 122.543 29.4117C124.496 28.1809 125.882 26.5665 126.7 24.5524H120.89C120.423 25.2078 119.738 25.7353 118.837 26.1669C117.952 26.5665 116.983 26.7583 115.915 26.7583C114.496 26.7583 113.26 26.3747 112.259 25.6234C111.24 24.8721 110.556 23.8491 110.205 22.5703L126.851 22.5543ZM115.731 14.7858C117.05 14.7858 118.202 15.1534 119.187 15.8887C120.206 16.624 120.89 17.5991 121.241 18.8299H110.255C110.606 17.5991 111.29 16.624 112.275 15.8887C113.277 15.1534 114.429 14.7858 115.731 14.7858Z" fill="white"/>
                  <path d="M24.29 1.59845C22.9877 0.879141 21.5853 0.335662 20.1161 -1.52588e-05L16.1593 7.20906L12.2024 0.0159692C9.3141 0.719294 6.6929 2.1739 4.60596 4.20395L8.64628 10.3261L2.9865 10.1183L0.849473 10.0383C0.649127 10.5818 0.498867 11.1573 0.365303 11.7167C-0.118867 13.8427 -0.118867 16.0486 0.348607 18.1905C0.465476 18.766 0.632431 19.3254 0.816082 19.8689L2.91972 19.789L7.72803 19.6132L5.15692 23.5134L4.02162 25.2398C4.42231 25.6713 4.8397 26.0869 5.29048 26.4866C6.97673 27.9571 8.96349 29.0761 11.1339 29.7474C11.7183 29.9233 12.3026 30.0831 12.8869 30.195L13.872 28.3887L16.1426 24.2647L18.4298 28.4047L19.4149 30.195C22.8709 29.5236 25.9929 27.7973 28.3303 25.2877L27.195 23.5454L24.0896 18.8619L31.7863 19.1496C32.5876 16.4002 32.571 13.491 31.7529 10.7577L23.1714 11.0774L27.7293 4.15599C26.7108 3.16495 25.5588 2.30177 24.29 1.59845ZM25.9595 24.8561C24.3401 26.3267 22.37 27.3977 20.233 28.0051L17.0608 22.2506L16.1593 20.6202L15.2577 22.2506L12.1023 27.9891C9.94853 27.3817 7.99515 26.2947 6.39238 24.7922L9.99862 19.3254L11.017 17.7749L9.09706 17.8389L2.40216 18.0946C1.8846 16.0166 1.90129 13.8587 2.41885 11.7807L14.3228 12.2282C15.3746 12.2602 16.393 12.5479 17.2946 13.0435C18.1961 13.539 18.9641 14.2423 19.5318 15.0895L25.9595 24.8561Z" fill="white"/>
                </g>
                <defs>
                  <clipPath id="clip0_5_61">
                    <rect width="223" height="50" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </a>
            {/* {isResources && <p>Resources</p>} */}
          </Logo>
          {
            windowSize.width < vars.navBreakpoint ? (
              <NavToggle />
            ) : (
              isResources ? (
                <Menu menuItems={data.MenuResources} />
              ) : (
                <>
                  <Menu menuItems={data.MenuCenter} />
                  <Menu menuItems={data.MenuRight} smallDropdown />
                </>
              )
            )
          }
        </Container>
      </HeaderWrap>
    </>
  );
};

export default Header;
